import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import moment, { Moment } from 'moment';
import styled, { css } from 'styled-components';
import { Translate } from 'react-localize-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import AppState from '@webapp/store/state';
import { Order } from '../../../types/orders/order';
import SimpleArtist from '../../../../../../common/types/artist/simple-artist';

import { getWidth } from '@components/styles';

import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';
import Grid from '@components/basics/grid';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import { Subscription } from '../../../../../../services/subscription/types/subscription';

interface PlanDowngradePageProps {
  className?: string;
  artist?: SimpleArtist;
  currentSubscription?: Subscription;
  currentOrder?: Order;
  currentPlan: string;
  features: string[];
  selectedPlan: string;
  expiresAt?: Moment;
  isMobile: boolean;
  goToConfirmation: () => CallHistoryMethodAction;
  goBack: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState, props: any) => {
  const { clientId, id } = props.match?.params;

  const foundSubscription = state.subscriptionManager.summary?.artists
    .find((s) => s.id === clientId)
    ?.subscriptions.find((s) => s.id === id);

  const currentOrder = state.orders.basket.find(
    (p) => p.product.category === 'PLAN'
  );

  return {
    currentSubscription: foundSubscription?.subscription,
    artist: state.artist.myArtists.find((a) => a.id === clientId),
    currentPlan: foundSubscription?.plan || '',
    features:
      state.products.list.find(
        (p) => p.category === 'PLAN' && p.name === foundSubscription?.plan
      )?.features || [],
    expiresAt: foundSubscription?.nextBilling
      ? moment(foundSubscription?.nextBilling)
      : undefined,
    currentOrder,
    selectedPlan: currentOrder?.product.name || '',
    isMobile: state.ui.mobile,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: any) => {
  const { clientId, id } = props.match?.params;

  return {
    goBack: () => dispatch(push(`/artist/subscriptions`)),
    goToConfirmation: () =>
      dispatch(push(`/plans/${clientId}/${id}/downgrade-confirmation`)),
  };
};

const PlanDowngradePage: React.FC<PlanDowngradePageProps> = ({ ...props }) => {
  const handleDowngrade = () => {
    props.goToConfirmation();
  };

  return (
    <MotionOpacity className={`${props.className || ''} mt-80 mb-50`}>
      <Container>
        <Typography className="mb-5" type="subheader">
          <Translate id="ONBOARDING.DOWNGRADE.TITLE" /> {props.selectedPlan}
        </Typography>
        <Typography className="mb-40" type="title" weight="400" opacity="0.5">
          <Translate id="ONBOARDING.DOWNGRADE.TEXT" />
        </Typography>

        <Card active={true}>
          <div className="header">
            <SelectedType type="title" weight="700" capitalize>
              <Translate id="ARTIST_PROFILE.PLANS.PRO" />
            </SelectedType>
            <PositionedType active={true} type="small" weight="700">
              <Translate id="ARTIST_PROFILE.PLANS.ACTIVE" />
            </PositionedType>
          </div>
          <ItemsBox>
            <InfoWrapper>
              {props.features.map((feature, i) => (
                <Item key={i}>{feature}</Item>
              ))}
            </InfoWrapper>
          </ItemsBox>
        </Card>

        <Card active={false}>
          <div className="header">
            <SelectedType type="title" weight="700" capitalize>
              <Translate id="ARTIST_PROFILE.PLANS.FREE" />
            </SelectedType>
            <PositionedType active={false} type="small" weight="700">
              <Translate id="ARTIST_PROFILE.PLANS.DOWNGRADED_PLAN" />
            </PositionedType>
          </div>
          <ItemsBox>
            <Table className="mb-0">
              <Row>
                <Typography className="col-1" type="small">
                  <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.ROYALTY" />
                </Typography>
                <Typography
                  className="col-2"
                  type="small"
                  align="center"
                  style={{ textDecoration: 'line-through' }}
                >
                  <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.ROYALTY_PRO" />
                </Typography>
                <Typography className="col-3" type="small" align="center">
                  <Translate id="ARTIST_PROFILE.PLANS.UNSUBSCRIBE.ITEM.ROYALTY_FREE" />
                </Typography>
              </Row>
            </Table>
          </ItemsBox>
        </Card>

        <CustomHint
          // TODO: Integrate date until the user can use the plan
          customTitle={
            <Translate
              id="ONBOARDING.PRICING.BILLING.HINT.TITLE"
              data={{
                date: moment(props.expiresAt).format('D MMM YYYY'),
              }}
            />
          }
        >
          <Translate id="ONBOARDING.PRICING.BILLING.HINT.TEXT" />
        </CustomHint>

        <ButtonGroup className="mt-40">
          <Button buttonType="primary" onClick={() => props.goBack()}>
            <Translate id="ONBOARDING.DOWNGRADE.BTN.CANCEL" />
          </Button>
          <Button
            buttonType="secondary"
            onClick={handleDowngrade}
            className="btn-downgrade"
          >
            <Translate id="ONBOARDING.DOWNGRADE.BTN.DOWNGRADE" />
          </Button>
        </ButtonGroup>
      </Container>
    </MotionOpacity>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(PlanDowngradePage);

const Container = styled.div`
  margin: auto;
  max-width: ${getWidth(6)};

  @media ${({ theme }) => theme.device.tablet} {
    max-width: ${getWidth(10)};
  }
`;

const CustomHint = styled(Hint)`
  margin-top: 0;
  border-radius: 0 0 10px 10px;
`;

const Card = styled.div<{ active: boolean }>`
  padding: 30px;
  position: relative;

  ${({ active }) =>
    !active
      ? css`
          border-radius: 2px 2px 0 0;
          background-color: ${({ theme }) => theme.colors.lightGray};
          span {
            color: ${({ theme }) => theme.colors.primary};
          }
        `
      : css`
          border-radius: 10px 10px 0 0;
          background-color: ${({ theme }) => theme.colors.primary};
          span {
            color: ${({ theme }) => theme.colors.background};
          }
        `}

  .header {
    margin-bottom: 24px;
  }
`;

const PositionedType = styled(Typography)<{ active: boolean }>`
  border-radius: 90px;
  padding: 4px 8px;
  max-width: 150px;
  transform: translate(-8px, 0);
  opacity: 1;
  text-align: center;
  position: absolute;
  top: 30px;
  right: 30px;
  color: ${({ theme }) => theme.colors.black} !important;
  ${({ active }) =>
    !active
      ? css`
          background-color: ${({ theme }) => theme.colors.hint};
        `
      : css`
          background-color: ${({ theme }) => theme.colors.greenLight};
        `}
`;

const SelectedType = styled(Typography)`
  color: ${({ theme }) => theme.colors.pureBlackWhite};
`;

const ItemsBox = styled(Flex)`
  flex-direction: column;
`;

const InfoWrapper = styled.div``;

const Item = styled(Typography)`
  margin-bottom: 14px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.tertiary} !important;

  &:last-child {
    margin-bottom: 0%;
  }
`;

const Table = styled(Grid)`
  .col-1 {
    grid-column: span 4;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .col-2 {
    grid-column: 5 / 7;
  }

  .col-3 {
    grid-column: 7 / 8;
  }

  .table-header {
    padding-bottom: 20px;
  }

  .table-footer {
    margin-top: 20px;
  }

  .table-header,
  .table-footer {
    align-items: center;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }
`;

const Row = styled(Grid)`
  padding: 10px 0;
  align-items: center;
  grid-template-columns: repeat(7, minmax(0, 1fr));
`;

const ButtonGroup = styled(Flex)`
  flex-direction: column;
  @media ${({ theme }) => theme.device.tablet} {
    flex-direction: row;
  }
  button {
    margin-bottom: 5px;
    &:last-child {
      margin-bottom: 0;
    }
    @media ${({ theme }) => theme.device.tablet} {
      width: 50%;
      margin-right: 5px;
      margin-bottom: 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .btn-downgrade span {
    color: ${({ theme }) => theme.colors.accent};
  }
`;
